import isEmpty from 'lodash-es/isEmpty';
import forEach from 'lodash-es/forEach';
import cloneDeep from 'lodash-es/cloneDeep';
import sortBy from 'lodash-es/sortBy';
import findIndex from 'lodash-es/findIndex';
import find from 'lodash-es/find';
import get from 'lodash-es/get';
import moment from 'moment/src/moment';
import head from 'lodash-es/head';
import trim from 'lodash-es/trim';
import without from 'lodash-es/without';
import uniq from 'lodash-es/uniq';
import * as webUtils from '@dreamworld/web-util';
import { diffLines, diffWords, diffChars, diffArrays } from 'diff';
import { createSelector } from 'reselect';
import { Timestamp } from "firebase/firestore";
import i18next from '@dw/i18next-esm';
import Handlebars from './lib/handlebars-esm.js';
import { parseReferenceLink, referenceTypeOf } from './redux/references/utils.js';
import { getDateEndTime } from './date-utils.js';

/**
 * Set focus to end of text
 * @param { Element } ele - Javascript element
 */
export const setFocusToEnd = (ele) => {
  if (typeof ele.selectionStart == "number") {
    ele.selectionStart = ele.selectionEnd = ele.value.length;
    ele.focus();
  } else if (typeof ele.createTextRange != "undefined") {
    ele.focus();
    let range = ele.createTextRange();
    range.collapse(false);
    range.select();
  }
}

/**
 * Validates email address.
 * @param {String} email Email address
 */
export const validateEmail = (email) => {
  var re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Get generated new uuid.
 * @public
 */
export const uuid = () => {
  let d = new Date().getTime();
  let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    let r = Math.random() * 16;

    if(d > 0){
        r = (d + r)%16 | 0;
        d = Math.floor(d/16);
    } else {
        r = (d2 + r)%16 | 0;
        d2 = Math.floor(d2/16);
    }

    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

/**
 * @param {String} cname - Give cookie name to return a cookie value
 * @returns {String} cookie value.
 */
export const getCookieVal = (cname) => {
  if (!cname) {
    return;
  }

  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document && document.cookie || '');
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return;
}

/**
 * Set cookie value.
 */
export const setCookieVal = (cname, cvalue, domain, expiresDays) => {
  if (!cname) {
    return;
  }

  const d = new Date();
  expiresDays = expiresDays || 30;
  d.setTime(d.getTime() + (expiresDays*24*60*60*1000));
  const expires = `expires=${d.toUTCString()}`;
  const domainStr = domain || window.location.host;
  document.cookie = `${cname}=${cvalue};path=/;domain=${domainStr};${expires}`;
}

/**
 * @param {Object} e passed event object of click.
 * @returns {String} if click is triggered from a tag then return url of a tag.
 */
export const findUrl = (e) => {
  let aPath = e.composedPath && e.composedPath() || e.path || [];
  let sNewUrl;
  if(!isEmpty(aPath)) {
    forEach(aPath, function(el) {
      if(el.tagName === 'A') {
        sNewUrl = el.href;
        return false;
      }
    });
  }

  let el = e.srcElement || e.target;
  if(!sNewUrl && el && el.tagName === 'A') {
    sNewUrl = el.href;
  }

  return sNewUrl;
}

/**
 * @return Time zone id e.g. IST|Indian Standard Time for India
 */
export const getTimezoneID = () => {
  let date = new Date();
  let dateStr = date.toString().trim();
  let timezoneId = "";
  let startIndex = dateStr.lastIndexOf('(');
  if (startIndex > 0)
      timezoneId = dateStr.substring(startIndex + 1, dateStr.lastIndexOf(')'));

  return timezoneId;
}

/**
 * Get current date based on timezone offset.
 * @param {Number} timezoneOffset - Timezone offset in minutes (e.g., -330 for IST, 0 for UTC)
 * @returns {Date} Current date adjusted for the given timezone offset
 */
export const getCurrentDateByTimezone = (timezoneOffset) => {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc + (timezoneOffset * 60000));
}

/**
 * Updates order of the moved items based on it's before/after items.
 * @param {Array} allItems all items of the list where item will be moved.
 * @param {Array} movedItems List of items which is moved by the user.
 * @param {String} before Id of the item before which moved items will be placed. If it's not given, items will be placed at bottom of the list.
 * @param {String} sortByKey key name by which items are sorted. It's optional. Default is 'order'
 * @returns {Array} Moved items with it's updated order.
 */
export const getNewOrder = ({ allItems, movedItems, before, sortByKey = 'order', ignoreAllItemsMove = false }) => {
  if (!allItems || isEmpty(movedItems)) {
    console.warn('getNewOrder: Mandatory fields are not provided', { allItems, movedItems });
    return;
  }

  if (!Array.isArray(allItems) || !Array.isArray(movedItems)) {
    console.warn('getNewOrder: Please provide valid array of allItems & movedItems', { allItems, movedItems });
    return;
  }

  // Clones items before mutations.
  let allItemsClone = cloneDeep(allItems);
  let movedItemsClone = cloneDeep(movedItems);
  allItemsClone = sortBy(allItemsClone, sortByKey);
  movedItemsClone = sortBy(movedItemsClone, sortByKey);

  // Removes moved items from the list.
  forEach(movedItemsClone, (item) => {
    const removeIndex = findIndex(allItemsClone, { id: item.id });
    if (removeIndex >= 0) {
      allItemsClone.splice(removeIndex, 1);
    }
  })

  // Inserts moved items at `before` index.
  const beforeIndex = before && find(allItemsClone, {id: before}) ? findIndex(allItemsClone, { id: before }) : undefined;
  const beforeItem = beforeIndex !== undefined ? get(allItemsClone, beforeIndex) : undefined;
  const beforeOrder = get(beforeItem, sortByKey);
  const afterIndex = beforeIndex === undefined ? (allItemsClone.length - 1) : beforeIndex ? beforeIndex - 1 : undefined;
  const afterItem = afterIndex !== undefined ? get(allItemsClone, afterIndex) : undefined;
  const afterOrder = get(afterItem, sortByKey);

  let orderDiffBetweenEachMovedItem;
  let startOrder;
  if(beforeOrder !== undefined && beforeOrder !== null && afterOrder !== undefined && afterOrder !== null){ // When item(s) moved between the list items.
    const median = Math.floor((beforeOrder + afterOrder) / 2);
    if (median > (afterOrder + movedItemsClone.length)) {
      orderDiffBetweenEachMovedItem = Math.floor((beforeOrder - afterOrder) / (movedItemsClone.length + 1));
      startOrder = afterOrder + orderDiffBetweenEachMovedItem;
    }
  } else if (beforeOrder !== undefined && beforeOrder !== null) { // When item(s) moved at top of the list.
    orderDiffBetweenEachMovedItem = 1000;
    startOrder = beforeOrder - (1000 * (movedItemsClone.length));
  } else if (afterOrder !== undefined && afterOrder !== null) {  // When item(s) moved at the bottom of the list.
    orderDiffBetweenEachMovedItem = 1000;
    startOrder = afterOrder + orderDiffBetweenEachMovedItem;
  }

  // Updates order of only moved items.
  if ((orderDiffBetweenEachMovedItem !== undefined && startOrder !== undefined) || ignoreAllItemsMove) {
    orderDiffBetweenEachMovedItem = orderDiffBetweenEachMovedItem || 1000;
    if(startOrder === undefined || startOrder === null) {
      const first = head(allItemsClone);
      const order = first && first[sortByKey] || 0;
      startOrder = order - 1000;
    }
    forEach(movedItemsClone, (item) => {
      item[sortByKey] = startOrder;
      startOrder = startOrder + orderDiffBetweenEachMovedItem
    })
    return movedItemsClone;
  }

  // Inserts moved items & updates order of all the items.
  allItemsClone.splice(beforeIndex, 0, ...movedItemsClone);
  let i = 0;
  forEach(allItemsClone, (item) => {
    item[sortByKey] = i;
    i = i + 1000;
  })

  return allItemsClone;
}


/**
 * @returns {Number} today timestamp.
 */
export const getTodayTime = () => {
  return window.parseInt(moment().endOf('day').format('x')) || null;
}

/**
 * @returns {Number} tommorrow timestamp
 */
export const getTomorrowTime = () => {
  return window.parseInt(moment().add(1, 'day').endOf('day').format('x')) || null;
}

/**
 * @returns {Number} End of this week timestamp.
 */
export const getEndOfThisWeekTime = () => {
  return window.parseInt(moment().add(1, 'week').startOf('week').endOf('day').format('x')) || null;
}

/**
 * @returns {Number} End of next week timestamp.
 */
export const getEndOfNextWeekTime = () => {
  return window.parseInt(moment().add(2, 'week').startOf('week').endOf('day').format('x')) || null;
}

/**
 * @returns {Number} End of this month timestamp.
 */
export const getEndOfThisMonthTime = () => {
  return window.parseInt(moment().endOf('month').format('x')) || null;
}

/**
 * @returns {Number} End of next month timestamp.
 */
export const getEndOfNextMonthTime = () => {
  return window.parseInt(moment().add(1, 'month').endOf('month').format('x')) || null;
}

/**
 * @returns {Number} End of this quarter timestamp.
 */
export const getEndOfThisQuarterTime = () => {
  return window.parseInt(moment().endOf('quarter').format('x')) || null;
}

/**
 * The instant a plainly picked day gets under the account's due date default.
 *  - `ZONE_END_OF_DAY` / `ZONE_TIME`: that day in the account's zone, at its end or at the chosen
 *    time, so one pick is the same moment for every member.
 *  - Anything else, an absent or malformed default included: the end of that day in the viewer's zone.
 * @param {String|Number} date `YYYY-MM-DD`, or any timestamp on that day in the viewer's zone
 * @param {Object} dueDateDefault `{ mode, zoneOffsetMinutes, timeOfDayMinutes }`
 * @returns {Number} epoch ms
 */
export const resolveDueTime = (date, dueDateDefault) => {
  const day = moment(date).format('YYYY-MM-DD');
  if (!isZoneDueDateDefault(dueDateDefault)) {
    return getDateEndTime(day);
  }

  // `zoneOffsetMinutes` is what is added to the zone's local time to get UTC (IST = -330).
  const zoneMidnight = moment.utc(day).valueOf() + dueDateDefault.zoneOffsetMinutes * 60000;
  if (dueDateDefault.mode === 'ZONE_TIME') {
    return zoneMidnight + dueDateDefault.timeOfDayMinutes * 60000;
  }
  return zoneMidnight + 24 * 60 * 60000 - 1;
}

/**
 * The instant an AI Helper tool's `dueDate` proposes, as the server stores it: a date-time
 * (`2026-10-30T14:00`, in the viewer's zone, or with an offset) keeps its time; a plain date gets
 * the account's due date default through `resolveDueTime`.
 * @param {String|Number} value the tool's `dueDate`, or a timestamp already resolved
 * @param {Object} dueDateDefault `{ mode, zoneOffsetMinutes, timeOfDayMinutes }`
 * @returns {Number|null} epoch ms; `null` for no date or an unreadable one
 */
export const resolveToolDueTime = (value, dueDateDefault) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  if (!String(value).includes('T')) {
    return moment(value, 'YYYY-MM-DD', true).isValid() ? resolveDueTime(value, dueDateDefault) : null;
  }
  const timestamp = moment(value).valueOf();
  return Number.isNaN(timestamp) ? null : timestamp;
}

const isZoneDueDateDefault = (dueDateDefault) => {
  const mode = dueDateDefault && dueDateDefault.mode;
  return (mode === 'ZONE_END_OF_DAY' || mode === 'ZONE_TIME')
    && Number.isFinite(dueDateDefault.zoneOffsetMinutes)
    && (mode !== 'ZONE_TIME' || Number.isFinite(dueDateDefault.timeOfDayMinutes));
}

/**
 * @param {Number} timestamp a due date
 * @param {Object} dueDateDefault the account's due date default
 * @returns {Boolean} `true` when the due date carries no time chosen by a member: it is the end of
 *  the viewer's day, or the time the account's zone default gives its day in that zone.
 */
export const isDefaultDueTime = (timestamp, dueDateDefault) => {
  if (isViewerEndOfDay(timestamp)) {
    return true;
  }
  if (!isZoneDueDateDefault(dueDateDefault)) {
    return false;
  }
  const accountDay = moment(timestamp).utcOffset(-dueDateDefault.zoneOffsetMinutes).format('YYYY-MM-DD');
  return resolveDueTime(accountDay, dueDateDefault) === timestamp;
}

/**
 * The instant a quick action or a picked day gets: the current due date's chosen time on that day,
 * so a card due at 2 PM moved to Friday is due Friday at 2 PM; else the account's default.
 * @param {String|Number} date `YYYY-MM-DD`, or any timestamp on that day in the viewer's zone
 * @param {Object} dueDateDefault the account's due date default
 * @param {Number} dueDate the current due date, if any
 * @returns {Number} epoch ms
 */
export const resolvePickTime = (date, dueDateDefault, dueDate) => {
  if (!dueDate || isDefaultDueTime(dueDate, dueDateDefault)) {
    return resolveDueTime(date, dueDateDefault);
  }
  const time = moment(dueDate);
  return moment(date).hours(time.hours()).minutes(time.minutes()).startOf('minute').valueOf();
}

/**
 * @param {Number} zoneOffsetMinutes minutes added to the zone's local time to get UTC (IST = -330)
 * @returns {String} e.g. `UTC+05:30`
 */
export const formatZoneOffset = (zoneOffsetMinutes) => {
  return 'UTC' + moment().utcOffset(-zoneOffsetMinutes).format('Z');
}

/**
 * @param {Number} timestamp
 * @returns {Boolean} `true` when the timestamp is at 11:59 PM in the viewer's zone - how a due
 *  date picked without a time has always been stored.
 */
export const isViewerEndOfDay = (timestamp) => {
  const time = moment(timestamp);
  return time.hour() === 23 && time.minute() === 59;
}

/**
 * Opens virtual keyboard.
 */
export const openVirtualKeyboard = () => {
  webUtils.openVirtualKeyboard();
}

/**
 * Returs a Promise which is resolved when the entry animation for the element is completed.
 * - ELement is identified from the event object (e.g. e.target)
 * - If the element has implemented animation (e.g. dw-icon-button), then returned Promise is resolved
 *   only after the animation is completed.
 * - In case, element hasn't implemented animation, then Promise is resolved immediately.
 *
 * Future Enhancement:
 * - Argument could be either an element or an event.
 * - Animatable element is searched from the event propagation path
 *
 * @param {Object} e An Event
 * @returns {Promise}
 */
export const waitForEntryAnimation = (e) => {
  if(e.target && e.target.waitForEntryAnimation) {
    return e.target.waitForEntryAnimation
  }
  return Promise.resolve();
}

/**
 * Checks if `value` is in `collection` or not.
 * @param {Array|Object|string} collection
 * @param {Array|Object|string} value
 * @returns {Boolean} if value is in collection then true, otherwise `false`.
 */
export const includes = (collection, value) => {
  if(typeof value === 'object') {
    let match = false;
    forEach(value, (word) => {
      if(collection.includes(word)) {
        match = true;
        return false;
      }
    });
    return match;
  }

  return collection.includes(value);
}

/**
 * @param {Number} timestamp - Passed time stamp value.
 * @param {String} format - Passed date format(optional). When not passed `format` arguments then default format use `'MMMM DD, YYYY h:mm a'`.
 * @return {String} - Return Date based on `timestamp` and `format` argument using moment library
 */
export const getFormatDate = (timestamp, format) => {
  if(!timestamp) {
    return '';
  }
  if(!format){
    format = 'MMMM DD, YYYY';
  }
  return moment(timestamp).format(format);
}

/**
 * @param {String} value
 * @returns {Boolean} given value is hash number e.g. `#123`.
 */
export const isHashNumber = (value) => {
  return value && /^\#\s{0,}[0-9]+$/.test(value);
}

/**
 * @param {String} value
 * @returns {String} remove empty spaces from string.
 *                   e.g. 1. input: ` #123 ` output: `#123`
 *                        2. input: `#   123` output: `#123`.
 */
export const removeSpaces = (value) => {
  return value ? value.replace(/\s/g, '') : '';
}

/**
 * @param{}
 *  @property {String} id Entity Id.
 *  @property {String} prefix Prefix which should be removed.
 * @returns {String} uuid without prefix.
 */
export const getIdWoPrefix = createSelector(
  ({id, prefix}) => id,
  ({id, prefix}) => prefix,
  (id, prefix)=> {
    return id && id.replace(`${prefix}`, '') || id;
  },
  {
    memoizeOptions: {
      maxSize: 5000
    }
  }
)

const containsAnchorTag = (str) => /<a\s+[^>]*>[\s\S]*<\/a>/i.test(str);

/**
 * Helper function to parse HTML string into ordered chunks (text nodes and link nodes)
 * @param {String} htmlStr - HTML string
 * @returns {Array} Array of chunks in order, each chunk is {type: 'text'|'link', content, element}
 */
const parseHtmlToChunks = (htmlStr) => {
  const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
  const chunks = [];

  const walkNodes = (node) => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
        chunks.push({ type: 'text', content: child.textContent, element: null });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === 'A') {
          chunks.push({
            type: 'link',
            content: child.textContent || '',
            href: child.getAttribute('href') || '',
            element: child.cloneNode(true)
          });
        } else {
          walkNodes(child);
        }
      }
    });
  };

  walkNodes(doc.body);
  return chunks;
};

const createEl = (tag, chunk, isLink) => {
  const el = document.createElement(tag);
  el.appendChild(document.createTextNode(' '));
  el.appendChild(isLink ? chunk : document.createTextNode(chunk));
  return el;
};

/**
 * @params {*}
 *  @property {String} oldString Old string
 *  @property {String} newString New String
 *  @property {String} by Difference by. Possible values: 'CHARS', 'WORD', 'LINE'. Default is 'WORD'.
 */
export const getTextDifference = createSelector(
  ({ oldString }) => oldString,
  ({ newString }) => newString,
  ({ by }) => by,
  (oldString = '', newString = '', by = 'WORD') => {
    const preEl = document.createElement('pre');
    const fragment = document.createDocumentFragment();

    if (containsAnchorTag(oldString) || containsAnchorTag(newString)) {
      const oldChunks = parseHtmlToChunks(oldString);
      const newChunks = parseHtmlToChunks(newString);
      const maxLen = Math.max(oldChunks.length, newChunks.length);

      for (let i = 0; i < maxLen; i++) {
        const old = oldChunks[i];
        const neu = newChunks[i];

        if (old && neu) {
          if (old.type === 'link' && neu.type === 'link' && 
              old.href === neu.href && old.content === neu.content) {
            fragment.appendChild(createEl('span', neu.element, true));
          } else if (old.type === 'text' && neu.type === 'text') {
            const diffFn = by === 'LINE' ? diffLines : by === 'CHAR' ? diffChars : diffWords;
            diffFn(old.content, neu.content).forEach(part => {
              const tag = part.added ? 'ins' : part.removed ? 'del' : 'span';
              fragment.appendChild(createEl(tag, part.value, false));
            });
          } else {
            fragment.appendChild(createEl('del', old.type === 'link' ? old.element : old.content, old.type === 'link'));
            fragment.appendChild(createEl('ins', neu.type === 'link' ? neu.element : neu.content, neu.type === 'link'));
          }
        } else if (old) {
          fragment.appendChild(createEl('del', old.type === 'link' ? old.element : old.content, old.type === 'link'));
        } else if (neu) {
          fragment.appendChild(createEl('ins', neu.type === 'link' ? neu.element : neu.content, neu.type === 'link'));
        }
      }
    } else {
      const diffFn = by === 'LINE' ? diffLines : by === 'CHAR' ? diffChars : diffWords;
      diffFn(oldString, newString).forEach(part => {
        const el = document.createElement(part.added ? 'ins' : part.removed ? 'del' : 'span');
        el.appendChild(document.createTextNode(' '));
        el.appendChild(document.createTextNode(part.value));
        fragment.appendChild(el);
      });
    }

    preEl.appendChild(fragment);
    return preEl.innerHTML;
  },
  {
    memoizeOptions: {
      maxSize: 200
    }
  }
);

const parser = new DOMParser();
const BLOCK_TAGS = new Set([
  'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'UL', 'OL', 'BLOCKQUOTE', 'TR', 'PRE', 'TABLE', 'HR'
]);

// Sentinel for an inline <br> within a block. A private-use char survives the
// word diff as ordinary text (and DOM escaping leaves it untouched), then is
// restored to a real <br> in the serialized diff output.
const BR_PLACEHOLDER = '\uE000';

// Each distinct chip in one diff run maps to a single private-use char,
// the same idea as BR_PLACEHOLDER: the word diff then treats a chip as one atomic
// token instead of dropping it (textContent) or splitting its markup, so a changed
// chip reads as the old chip removed + the new one added. Reset per
// diffHtmlByBlock; restored to the chip's markup in the serialized output.
const CHIP_PLACEHOLDER_START = 0xE001;
let _chipPlaceholderByReference = new Map();
let _chipHtmlByPlaceholder = new Map();

/**
 * The `type:id` a chip token stands for, else null: a mention `chip-item`, a reference anchor (`<a data-type data-id>`),
 * or a plain link to a Kerika page, as the AI's Markdown writes an unchanged reference.
 */
const _chipReference = (element) => {
  if (element.tagName === 'CHIP-ITEM') {
    return `${(element.getAttribute('item-type') || 'USER').toLowerCase()}:${element.hasAttribute('all-user') ? 'all' : element.getAttribute('item-id')}`;
  }
  if (element.tagName !== 'A') return null;
  if (element.hasAttribute('data-type') && element.hasAttribute('data-id')) {
    return `${referenceTypeOf(element.getAttribute('data-type')).toLowerCase()}:${element.getAttribute('data-id')}`;
  }
  const link = parseReferenceLink(element.getAttribute('href') || '');
  return link ? `${link.type.toLowerCase()}:${link.id}` : null;
};

/** One placeholder per reference, so the same card or user reads unchanged whatever its link text or chip markup. */
const _chipPlaceholder = (chip) => {
  const reference = _chipReference(chip);
  let placeholder = _chipPlaceholderByReference.get(reference);
  if (!placeholder) {
    placeholder = String.fromCharCode(CHIP_PLACEHOLDER_START + _chipPlaceholderByReference.size);
    _chipPlaceholderByReference.set(reference, placeholder);
    _chipHtmlByPlaceholder.set(placeholder, chip.outerHTML);
  }
  return placeholder;
};

/**
 * A block's text with the line breaks and spaces it ends on dropped, for deciding whether anything
 * in it changed. A rewrite that only adds a trailing break has changed nothing a reader is looking
 * for, and counting it as a change flattened the whole line's bold and links to show that nothing
 * had happened to it.
 */
const blockBodyText = (text) => (text || '').replace(/[\uE000\s]+$/, '');

// One name per format, whichever side wrote it: the editor writes `<b>` / `<i>` / `<s>`, Markdown `<strong>` / `<em>` / `<del>`.
const FORMAT_BY_TAG = { B: 'b', STRONG: 'b', I: 'i', EM: 'i', U: 'u', S: 's', STRIKE: 's', DEL: 's', CODE: 'code' };

/**
 * A block's text run by run, with each run's formats, highlight and link target, so a rewrite that only changes formatting is
 * still a change, while the editor's and Markdown's markup for the same look compare equal. Spaces are dropped.
 */
const blockFormatting = (blockElement) => {
  const runs = [];
  const walk = (node, formats) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent.replace(/\s+/g, '');
        if (!text) continue;
        const format = [...new Set(formats)].sort().join(' ');
        const lastRun = runs[runs.length - 1];
        if (lastRun?.format === format) lastRun.text += text;
        else runs.push({ format, text });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (_chipReference(child)) runs.push({ format: '', text: _chipPlaceholder(child) });
        else if (child.tagName === 'A') walk(child, [...formats, `a:${child.getAttribute('href')}`]);
        else {
          // The editor saves a highlight as a span's background colour, which the browser reads back in one `rgb()` form.
          const highlight = child.style?.backgroundColor;
          const childFormats = [FORMAT_BY_TAG[child.tagName], highlight && `highlight:${highlight}`].filter(Boolean);
          walk(child, [...formats, ...childFormats]);
        }
      }
    }
  };
  walk(blockElement, []);
  return runs.map(({ format, text }) => `${format}|${text}`).join('\n');
};

/** Whether two blocks are the same kind of line: one heading level, one list kind, or a plain `<div>` / `<p>` line. */
const isSameBlockKind = (oldBlock, newBlock) => {
  const blockKind = ({ tag, list }) => `${tag === 'P' ? 'DIV' : tag}${list || ''}`;
  return blockKind(oldBlock) === blockKind(newBlock);
};

/** A block's text content with inline <br>s preserved as BR_PLACEHOLDER and
 *  chips preserved as their placeholder char. */
const blockTextWithBreaks = (el) => {
  const clone = el.cloneNode(true);
  for (const br of clone.querySelectorAll('br')) br.replaceWith(BR_PLACEHOLDER);
  for (const chip of clone.querySelectorAll('chip-item, a')) if (_chipReference(chip)) chip.replaceWith(_chipPlaceholder(chip));
  return clone.textContent.trim();
};

/**
 * Parses an HTML string into an ordered list of block descriptors, flattening
 * inline formatting to text:
 *
 *   { tag, list, text, formatting }
 *     tag        - block element tag ('DIV' | 'P' | 'LI' | 'H1' …)
 *     list       - parent list tag ('UL' | 'OL') when the block is an <li>, else null
 *     text       - the block's text content (inline marks like <b>/<a> dropped)
 *     formatting - the block's text with its inline formats (see `blockFormatting`)
 *
 * @param {String} html
 * @returns {Array<{tag: string, list: ?string, text: string, formatting: string}>}
 */
const parseHtmlBlocks = (html) => {
  if (!html || typeof html !== 'string') return [];
  const blocks = [];
  const walk = (node, list) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent.trim();
        if (text) blocks.push({ tag: 'DIV', list: null, text, formatting: `|${text.replace(/\s+/g, '')}` });
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const { tagName: tag } = child;
      const text = blockTextWithBreaks(child);
      // `html` is the block's own markup, kept so an unchanged block can be emitted as it was
      // written rather than as the word diff's flattened text.
      if (tag === 'UL' || tag === 'OL') walk(child, tag);
      else if (tag === 'LI') blocks.push({ tag: 'LI', list: list || 'UL', text, html: child.innerHTML, formatting: blockFormatting(child) });
      else if (BLOCK_TAGS.has(tag)) blocks.push({ tag, list: null, text, html: child.innerHTML, formatting: blockFormatting(child) });
      else walk(child, list);
    }
  };
  walk(parser.parseFromString(html, 'text/html').body, null);
  return blocks;
};

/** Re-serialises diffed blocks to HTML, grouping consecutive <li>s into their list. */
const serializeBlocks = (blocks) => {
  let html = '';
  let openList = null;
  const closeList = () => {
    if (openList) {
      html += `</${openList.toLowerCase()}>`;
      openList = null;
    }
  };
  for (const { tag, list, inner } of blocks) {
    if (list) {
      if (openList !== list) {
        closeList();
        html += `<${list.toLowerCase()}>`;
        openList = list;
      }
      html += `<li>${inner}</li>`;
    } else {
      closeList();
      const t = tag.toLowerCase();
      html += `<${t}>${inner}</${t}>`;
    }
  }
  closeList();
  return html;
};

/**
 * Word-diffs two HTML descriptions, keeping each block (div / li / heading) in
 * its original wrapper. A block nothing changed in keeps its own markup, so the
 * bold, links and headings of the untouched majority of a long value survive;
 * a block whose words stay but whose formatting changes shows as the old block
 * removed and the new one added; inside a block whose words change, inline
 * formatting flattens to text. Returns HTML with
 * span/ins/del runs, ready for `unsafeHTML`.
 *
 * @param {String} oldHtml
 * @param {String} newHtml
 * @returns {String} diffed HTML
 */
export const diffHtmlByBlock = (oldHtml, newHtml) => {
  _chipPlaceholderByReference = new Map();
  _chipHtmlByPlaceholder = new Map();
  const oldBlocks = parseHtmlBlocks(oldHtml);
  const newBlocks = parseHtmlBlocks(newHtml);
  const parts = diffArrays(oldBlocks.map((b) => b.text), newBlocks.map((b) => b.text));

  // Align blocks into [oldBlock?, newBlock?] pairs. A changed run (removed
  // immediately followed by added) pairs positionally so each block word-diffs
  // inline; pure adds / removes pair against null.
  const pairs = [];
  let oi = 0;
  let ni = 0;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.removed && parts[i + 1]?.added) {
      const removed = part.value.length;
      const added = parts[++i].value.length;
      // Past the shorter side, pair against null: the next block belongs to the unchanged run that follows.
      for (let j = 0; j < Math.max(removed, added); j++) {
        pairs.push([j < removed ? oldBlocks[oi + j] : null, j < added ? newBlocks[ni + j] : null]);
      }
      oi += removed;
      ni += added;
    } else if (part.removed) {
      for (let j = 0; j < part.value.length; j++) pairs.push([oldBlocks[oi++], null]);
    } else if (part.added) {
      for (let j = 0; j < part.value.length; j++) pairs.push([null, newBlocks[ni++]]);
    } else {
      for (let j = 0; j < part.value.length; j++) pairs.push([oldBlocks[oi++], newBlocks[ni++]]);
    }
  }

  // `getTextDifference` emits the right span/ins/del runs (and escapes) for
  // every case — changed, pure-add ('' → text), pure-remove (text → '') and
  // unchanged — so one call covers all of them.
  const rows = pairs.flatMap(([ob, nb]) => {
    const block = nb || ob;
    const isSameText = ob && nb && blockBodyText(ob.text) === blockBodyText(nb.text) && nb.html !== undefined;
    // A block nothing changed in keeps its own markup. The word diff works on text, so putting every
    // block through it stripped the bold, the links and the headings from lines that were never
    // touched — most of a long message, read as though the proposal had flattened it.
    if (isSameText && ob.formatting === nb.formatting && isSameBlockKind(ob, nb)) {
      return { tag: block.tag, list: block.list, inner: nb.html };
    }

    // Same words, new formatting or line kind: the word diff would show nothing, so show the old line removed and the new one added.
    if (isSameText && ob.html !== undefined) {
      return [
        { tag: ob.tag, list: ob.list, inner: `<del>${ob.html}</del>`, isFormattingRemoved: true },
        { tag: nb.tag, list: nb.list, inner: `<ins>${nb.html}</ins>`, isFormattingAdded: true },
      ];
    }

    // A block that is wholly added or wholly removed keeps its markup too, inside the run that says
    // which it is. The word diff would otherwise flatten a whole new description to plain text and
    // lose every heading, list and bold in it.
    if (!ob && nb && nb.html !== undefined) {
      return { tag: block.tag, list: block.list, inner: `<ins>${nb.html}</ins>` };
    }
    if (!nb && ob && ob.html !== undefined) {
      return { tag: block.tag, list: block.list, inner: `<del>${ob.html}</del>` };
    }

    // Restore <br>s preserved through the word diff (see BR_PLACEHOLDER).
    let inner = getTextDifference({ oldString: ob?.text || '', newString: nb?.text || '' })
      .split(BR_PLACEHOLDER)
      .join('<br>');
    // Restore chips preserved through the word diff (see _chipPlaceholder).
    // A removed chip lands inside the diff's <del>, an added one inside <ins>.
    for (const [ph, html] of _chipHtmlByPlaceholder) inner = inner.split(ph).join(html);
    return { tag: block.tag, list: block.list, inner };
  });

  // In a run of formatting-only changes, all old lines come first, then all new ones, so a list that changes kind reads as one list each.
  const orderedRows = [];
  let formattingAddedRows = [];
  for (const row of rows) {
    if (row.isFormattingAdded) {
      formattingAddedRows.push(row);
      continue;
    }
    if (!row.isFormattingRemoved) {
      orderedRows.push(...formattingAddedRows);
      formattingAddedRows = [];
    }
    orderedRows.push(row);
  }
  orderedRows.push(...formattingAddedRows);
  return serializeBlocks(orderedRows);
};

/**
 * @returns {Number} convert firestore date to js timestamp.
 */
export const convertFirestoreTimestamp = (seconds, nanoseconds) => {
  try {
    if(!nanoseconds) {
      return seconds * 1000;
    }
    const __timestamp = new Timestamp(seconds, nanoseconds);
    return new Date(__timestamp.toDate()).getTime();
  } catch (error) {
    return seconds * 1000;
  }
}

/**
 * @param {String} email multiple emails, seprated by comma(,), space, or enter(\n).
 * @returns {Array} emails array.
 */
export const getEmails = (email) => {
  email = trim(email);
  let emails = email.split(/(?:,| |\n)+/);
  emails = without(emails, '');
  emails = uniq(emails);
  return emails || [];
}

/**
 * Validate multiple emails.
 */
export const validateEmails = (emails) => {
  emails = typeof emails === 'string' ? getEmails(emails) : emails || '';
  if (isEmpty(emails)) {
    return false;
  }

  let allValidEmails = true;
  forEach(emails, (email) => {
    if (!validateEmail(email)) {
      allValidEmails = false;
      return false;
    }
  });
  return allValidEmails;
};

/*
 * @returns {String} language code before `_`.
 */
export const getLanguageCode = (code) => {
  return code && code.split('_')[0] || '';
}

/**
 * Validates if a name matches the required pattern for user names.
 * Pattern: No forward slashes or periods, 1-maxLength characters
 * @param {String} name - The name to validate
 * @param {Number} maxLength - Maximum length allowed (default: 20)
 * @returns {Boolean} true if valid, false otherwise
 */
export const validName = (name, maxLength = 50) => {
  if (!name) return false;
  const trimmedName = name.trim();
  if (trimmedName.length === 0 || trimmedName.length > maxLength) return false;
  const pattern = new RegExp(getNameAllowedPattern());
  return pattern.test(trimmedName);
};

/**
 * Gets the allowed pattern for name validation (excludes forward slashes and periods).
 * @returns {String} allowed pattern string
 */
export const getNameAllowedPattern = () => {
  return '^[^\\/\\.]+$';
};

/**
 * Resolves a unified template string using Handlebars.
 *
 * Supports:
 * - Simple strings: "Delete this board" → "Delete this board"
 * - Pure i18n: "{{i18n 'key'}}" → translated text
 * - i18n with params: "{{i18n 'key' count=5}}" → translated text with interpolation
 * - Variables: "{{variableName}}" → value from context
 * - Mixed: "{{i18n 'greeting'}} {{userName}}" → "Hello John"
 *
 * @param {String} template - Template string with optional Handlebars syntax
 * @param {Object} context - Variables to substitute in the template
 * @returns {String} Resolved template string
 *
 * @example
 * // Simple string
 * resolveTemplate("Hello World", {}) // → "Hello World"
 *
 * @example
 * // i18n key
 * resolveTemplate("{{i18n 'common:greeting'}}", {}) // → "Hello" (from i18n)
 *
 * @example
 * // i18n with params
 * resolveTemplate("{{i18n 'files.count' count=5}}", {}) // → "5 files"
 *
 * @example
 * // Variable substitution
 * resolveTemplate("Hello {{userName}}", { userName: "John" }) // → "Hello John"
 */
export const resolveTemplate = (template, context = {}) => {
  if (!template || typeof template !== 'string') {
    return template || '';
  }

  // Register i18n helper if not already registered
  if (!Handlebars.helpers.i18n) {
    Handlebars.registerHelper('i18n', function(key, options) {
      return i18next.t(key, options.hash);
    });
  }

  // Compile and render template
  const compiled = Handlebars.compile(template);
  return compiled(context);
};

/**
 * Resolves board tag models to a `{ id, name, color }` lookup keyed by tag id.
 * Shared by card-tags-diff and card-tags-diff-dialog so the resolution (and any
 * future fallback for deleted tags) lives in one place.
 *
 * @param {Array} boardTags board tag models (`{ tagId, name, tag: { color } }`).
 * @returns {Map<string, { id: string, name: string, color: string }>}
 */
export const tagModelsById = (boardTags) => {
  const byId = new Map();
  for (const m of boardTags || []) {
    byId.set(m.tagId, { id: m.tagId, name: m.name, color: m.tag.color });
  }
  return byId;
};
